import type { IsolationReport } from './isolatedObjects'
/**
 * CSV Parser for CAD/DXF Export Data
 *
 * Parses parking lot CSV files exported from CAD/DXF with entity data.
 * Supports various CSV formats and handles large files efficiently.
 */

export interface ParsedEntity {
  layer: string
  entityType: string
  entityHandle: string
  points: Array<{ x: number; y: number }>
  text?: string
  blockName?: string
  rotation?: number
  parkingType?: string // Parking space type from 9th column
  // Worker/parser contract: these flags must be preserved through worker conversion
  // so renderer can pick proper symbol/geometry behavior.
  isPoint?: boolean // Point-like entity (single coordinate or collapsed shape)
  isClosed?: boolean // Closed polyline/polygon hint from source parser
}

export interface ParseResult {
  isolationReport?: IsolationReport
  entities: ParsedEntity[]
  rowCount: number
  errors: string[]
}

/**
 * Required columns for entity-per-row format (legacy)
 */
const REQUIRED_COLUMNS_ENTITY = ['layer', 'entitytype', 'entityhandle', 'points'] as const

/**
 * Required columns for vertex-per-row format (DXF CSV output)
 * DXF CSV: X,Y,Z,Layer,PaperSpace,SubClasses,Linetype,EntityHandle,Text,OGR_STYLE,IsPoint
 */
const REQUIRED_COLUMNS_VERTEX = ['x', 'y', 'layer', 'entityhandle'] as const

/**
 * Parse points string to coordinate array
 *
 * Handles various formats:
 * - "[(100.5, 200.3), (150.2, 300.1)]"
 * - "(100.5, 200.3), (150.2, 300.1)"
 * - "(100.5, 200.3)"
 * - "100.5, 200.3"
 */
function parsePoints(pointsStr: string): Array<{ x: number; y: number }> {
  if (!pointsStr || pointsStr.trim() === '') {
    return []
  }

  try {
    // Remove outer brackets if present
    let cleaned = pointsStr.trim()
    if (cleaned.startsWith('[') && cleaned.endsWith(']')) {
      cleaned = cleaned.slice(1, -1)
    }

    // Match coordinate pairs: (x, y) or x, y
    // Regex: optional opening paren, number (int/float), comma, number, optional closing paren
    const coordPattern = /\(?\s*(-?\d+\.?\d*)\s*,\s*(-?\d+\.?\d*)\s*\)?/g
    const matches = [...cleaned.matchAll(coordPattern)]

    if (matches.length === 0) {
      return []
    }

    return matches.map(match => ({
      x: parseFloat(match[1]),
      y: parseFloat(match[2])
    }))
  } catch (error) {
    throw new Error(`Invalid points format: ${pointsStr}`)
  }
}

/**
 * Detect CSV delimiter (comma or tab)
 */
function detectDelimiter(headerLine: string): string {
  const commaCount = (headerLine.match(/,/g) || []).length
  const tabCount = (headerLine.match(/\t/g) || []).length

  return tabCount > commaCount ? '\t' : ','
}

/**
 * Parse CSV row with proper handling of quoted fields
 */
function parseCSVRow(line: string, delimiter: string): string[] {
  const fields: string[] = []
  let currentField = ''
  let inQuotes = false

  for (let i = 0; i < line.length; i++) {
    const char = line[i]
    const nextChar = line[i + 1]

    if (char === '"') {
      if (inQuotes && nextChar === '"') {
        // Escaped quote
        currentField += '"'
        i++ // Skip next quote
      } else {
        // Toggle quote mode
        inQuotes = !inQuotes
      }
    } else if (char === delimiter && !inQuotes) {
      // End of field
      fields.push(currentField.trim())
      currentField = ''
    } else {
      currentField += char
    }
  }

  // Add last field
  fields.push(currentField.trim())

  return fields
}

/**
 * Normalize column name for case-insensitive matching
 */
function normalizeColumnName(name: string): string {
  return name.toLowerCase().trim().replace(/[^a-z0-9]/g, '')
}

/**
 * Create column index mapping from header
 */
function createColumnMapping(headers: string[]): Map<string, number> {
  const mapping = new Map<string, number>()

  headers.forEach((header, index) => {
    const normalized = normalizeColumnName(header)
    mapping.set(normalized, index)
  })

  return mapping
}

/**
 * Detect CSV format: 'vertex-per-row' (DXF output) or 'entity-per-row' (legacy)
 */
function detectCsvFormat(columnMapping: Map<string, number>): 'vertex' | 'entity' {
  const hasXY = columnMapping.has('x') && columnMapping.has('y')
  const hasPoints = columnMapping.has('points')
  return (hasXY && !hasPoints) ? 'vertex' : 'entity'
}

/**
 * Validate that all required columns exist for the detected format
 */
function validateColumns(columnMapping: Map<string, number>): { errors: string[]; format: 'vertex' | 'entity' } {
  const format = detectCsvFormat(columnMapping)
  const required = format === 'vertex' ? REQUIRED_COLUMNS_VERTEX : REQUIRED_COLUMNS_ENTITY
  const errors: string[] = []

  required.forEach(requiredCol => {
    if (!columnMapping.has(requiredCol)) {
      errors.push(`Required column '${requiredCol}' not found`)
    }
  })

  return { errors, format }
}

/**
 * Extract entity type from SubClasses field
 * e.g. "AcDbEntity:AcDbPolyline" → "LWPOLYLINE"
 *      "AcDbEntity:AcDbPoint" → "POINT"
 */
function extractEntityTypeFromSubClasses(subClasses: string): string {
  if (!subClasses) return 'LWPOLYLINE'
  const lower = subClasses.toLowerCase()
  if (lower.includes('point')) return 'POINT'
  if (lower.includes('polyline')) return 'LWPOLYLINE'
  if (lower.includes('circle')) return 'CIRCLE'
  if (lower.includes('line')) return 'LINE'
  return 'LWPOLYLINE'
}

/**
 * Parse vertex-per-row CSV (DXF output format).
 * Groups rows by EntityHandle and assembles points arrays.
 */
function parseVertexPerRow(
  lines: string[],
  delimiter: string,
  columnMapping: Map<string, number>
): { entities: ParsedEntity[]; errors: string[] } {
  const errors: string[] = []
  // Group vertices by entity handle
  const entityMap = new Map<string, {
    layer: string
    subClasses: string
    text: string
    isPoint: boolean
    points: Array<{ x: number; y: number }>
  }>()
  // Preserve insertion order
  const handleOrder: string[] = []

  const getIdx = (col: string): number => columnMapping.get(col) ?? -1
  const xIdx = getIdx('x')
  const yIdx = getIdx('y')
  const layerIdx = getIdx('layer')
  const handleIdx = getIdx('entityhandle')
  const subClassesIdx = getIdx('subclasses')
  const textIdx = getIdx('text')
  const isPointIdx = getIdx('ispoint')

  for (let i = 1; i < lines.length; i++) {
    const line = lines[i]
    if (!line.trim()) continue

    const fields = parseCSVRow(line, delimiter)
    const rowNumber = i + 1

    const xStr = xIdx >= 0 ? fields[xIdx] : ''
    const yStr = yIdx >= 0 ? fields[yIdx] : ''
    const layer = layerIdx >= 0 ? (fields[layerIdx] || '').trim() : ''
    const handle = handleIdx >= 0 ? (fields[handleIdx] || '').trim() : ''

    if (!layer || !handle) {
      errors.push(`Row ${rowNumber}: Missing layer or entityHandle`)
      continue
    }

    const x = parseFloat(xStr)
    const y = parseFloat(yStr)
    if (isNaN(x) || isNaN(y)) {
      errors.push(`Row ${rowNumber}: Invalid coordinates X=${xStr}, Y=${yStr}`)
      continue
    }

    if (!entityMap.has(handle)) {
      entityMap.set(handle, {
        layer,
        subClasses: subClassesIdx >= 0 ? (fields[subClassesIdx] || '') : '',
        text: textIdx >= 0 ? (fields[textIdx] || '') : '',
        isPoint: isPointIdx >= 0 ? (fields[isPointIdx] || '').toLowerCase() === 'true' : false,
        points: []
      })
      handleOrder.push(handle)
    }

    entityMap.get(handle)!.points.push({ x, y })
  }

  // Convert grouped data to ParsedEntity array
  const entities: ParsedEntity[] = []
  for (const handle of handleOrder) {
    const data = entityMap.get(handle)!
    const entityType = extractEntityTypeFromSubClasses(data.subClasses)

    const entity: ParsedEntity = {
      layer: data.layer,
      entityType,
      entityHandle: handle,
      points: data.points
    }

    if (data.isPoint || data.points.length === 1) {
      entity.isPoint = true
    }

    // Detect closed polygons: first and last point are the same
    if (data.points.length >= 3) {
      const first = data.points[0]
      const last = data.points[data.points.length - 1]
      if (Math.abs(first.x - last.x) < 0.001 && Math.abs(first.y - last.y) < 0.001) {
        entity.isClosed = true
        // Remove duplicate closing point
        entity.points = data.points.slice(0, -1)
      }
    }

    if (data.text) {
      entity.text = data.text
    }

    entities.push(entity)
  }

  return { entities, errors }
}

/**
 * Parse a single entity row
 */
function parseEntityRow(
  fields: string[],
  columnMapping: Map<string, number>,
  rowNumber: number
): { entity?: ParsedEntity; error?: string } {
  try {
    const getField = (colName: string): string => {
      const index = columnMapping.get(colName)
      if (index === undefined) return ''
      return fields[index] || ''
    }

    const layer = getField('layer')
    const entityType = getField('entitytype')
    const entityHandle = getField('entityhandle')
    const pointsStr = getField('points')

    // Validate required fields
    if (!layer) {
      return { error: `Row ${rowNumber}: Missing layer` }
    }
    if (!entityType) {
      return { error: `Row ${rowNumber}: Missing entityType` }
    }
    if (!entityHandle) {
      return { error: `Row ${rowNumber}: Missing entityHandle` }
    }

    // Parse points
    let points: Array<{ x: number; y: number }> = []
    try {
      points = parsePoints(pointsStr)
    } catch (err) {
      return {
        error: `Row ${rowNumber}: ${err instanceof Error ? err.message : 'Invalid points format'}`
      }
    }

    // Build entity
    const entity: ParsedEntity = {
      layer,
      entityType,
      entityHandle,
      points
    }

    // Detect point entities
    // Point entities are those with:
    // 1. Only one coordinate point
    // 2. Entity types like POINT, INSERT, TEXT, CIRCLE with radius 0
    // 3. Entities where all points are at the same coordinate
    if (points.length === 1 ||
        entityType.toUpperCase() === 'POINT' ||
        entityType.toUpperCase() === 'INSERT' ||
        entityType.toUpperCase() === 'TEXT' ||
        (points.length > 1 && points.every(p => p.x === points[0].x && p.y === points[0].y))) {
      entity.isPoint = true
    }

    // Optional fields
    const text = getField('text')
    if (text) {
      entity.text = text
    }

    const blockName = getField('blockname')
    if (blockName) {
      entity.blockName = blockName
    }

    const rotationStr = getField('rotation')
    if (rotationStr) {
      const rotation = parseFloat(rotationStr)
      if (!isNaN(rotation)) {
        entity.rotation = rotation
      }
    }

    // Extract parking type from 9th column (index 8)
    // Column names might vary, so we try multiple possible names
    const parkingType = getField('parkingtype') || getField('type') || getField('spottype')
    if (parkingType) {
      entity.parkingType = parkingType.trim().toUpperCase()
    }

    return { entity }
  } catch (error) {
    return {
      error: `Row ${rowNumber}: ${error instanceof Error ? error.message : 'Parse error'}`
    }
  }
}

/**
 * Main CSV parser function
 *
 * @param csvContent - Raw CSV file content
 * @returns ParseResult with entities and errors
 */
export function parseCSV(csvContent: string): ParseResult {
  const errors: string[] = []
  const entities: ParsedEntity[] = []

  // Validate input
  if (!csvContent || csvContent.trim() === '') {
    return {
      entities: [],
      rowCount: 0,
      errors: ['CSV file is empty']
    }
  }

  // Split into lines (handle both \n and \r\n)
  const lines = csvContent.split(/\r?\n/).filter(line => line.trim())

  if (lines.length === 0) {
    return {
      entities: [],
      rowCount: 0,
      errors: ['CSV file contains no data']
    }
  }

  if (lines.length === 1) {
    return {
      entities: [],
      rowCount: 0,
      errors: ['CSV file contains only header row']
    }
  }

  // Parse header
  const headerLine = lines[0]
  const delimiter = detectDelimiter(headerLine)
  const headers = parseCSVRow(headerLine, delimiter)

  if (headers.length < 4) {
    return {
      entities: [],
      rowCount: 0,
      errors: [`CSV has too few columns. Expected at least 4, found ${headers.length}`]
    }
  }

  // Create column mapping
  const columnMapping = createColumnMapping(headers)

  // Validate required columns and detect format
  const { errors: columnErrors, format } = validateColumns(columnMapping)
  if (columnErrors.length > 0) {
    return {
      entities: [],
      rowCount: lines.length - 1,
      errors: columnErrors
    }
  }

  // Use format-specific parser
  if (format === 'vertex') {
    const result = parseVertexPerRow(lines, delimiter, columnMapping)
    return {
      entities: result.entities,
      rowCount: lines.length - 1,
      errors: [...errors, ...result.errors]
    }
  }

  // Entity-per-row format (legacy)
  for (let i = 1; i < lines.length; i++) {
    const line = lines[i]
    const rowNumber = i + 1 // 1-based row numbers (after header)

    // Skip empty lines
    if (!line.trim()) {
      continue
    }

    const fields = parseCSVRow(line, delimiter)

    // Parse entity
    const result = parseEntityRow(fields, columnMapping, rowNumber)

    if (result.error) {
      errors.push(result.error)
      // Continue parsing other rows for better error reporting
    } else if (result.entity) {
      entities.push(result.entity)
    }
  }

  return {
    entities,
    rowCount: lines.length - 1, // Exclude header
    errors
  }
}
